// React
import { Component } from "react";
import _ from 'lodash';

// Internal components
import { DebugEx } from "@Utils/DebugEx"
import { TimeSeriesSavedDataChartProps, TSViewType, TSChartType } from "./TimeSeriesSavedDataStructs";
import { ExcelTimezone, ExcelExportFormat } from './TimeSeriesStructs';
import { IWidgetSettingsProps } from '@UI/Widgets/WidgetStructs';
import { getRandomSmallKey, isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { eIconType, SettingsGroup, SettingsType } from '@Primitive/SettingsListType';
import { ToolbarIconInfo, ToolbarIconInfo_GetSettingList } from '@Primitive/ToolbarIconButton';
import { Localization } from '@Primitive/Localization/Localization';
import SettingsList from '@Primitive/SettingsList';
import { GetTSSeriesSavedDataSettingsLocalizationDictionary, TSSavedDataChartUILabelKey, TSSeriesSavedDataSettingsLocalizationTextMapKey } from './TimeSeriesSavedDataChartLocalization';
// =======================================================================================================
export interface TSSavedDataChartSettingsState {
    widgetData: TimeSeriesSavedDataChartProps;
}
// =======================================================================================================
export default class TSSavedDataChartSettings extends Component<IWidgetSettingsProps<TimeSeriesSavedDataChartProps>, TSSavedDataChartSettingsState> {
    // ---------------------------------------------------------------------------------------------------
    constructor(props: IWidgetSettingsProps<TimeSeriesSavedDataChartProps>) {
        super(props);
        this.state = {
            widgetData: _.cloneDeep(this.props.WidgetData),
        };
    }
    // ---------------------------------------------------------------------------------------------------
    private saveData() {
        if (isDefined(this.props.onWidgetDataUpdate)) {
            this.props.onWidgetDataUpdate(this.state.widgetData);
        }
    }
    // ---------------------------------------------------------------------------------------------------
    render() {
        let labels = this._GetLocalizedLabels(this.props.LocalizationKey);
        let settings: SettingsGroup[] = [
            {
                name: "Toolbar",
                elements: [
                    {
                        type: SettingsType.ItemsMgmtDialog,
                        label: "Toolbar Icons",
                        tooltip: "Extra custom icons in toolbar",
                        dataPath: "customToolbarIcons",
                        itemsInfo: ToolbarIconInfo_GetSettingList(),
                        createNewItem: () => {
                            return {
                                icon: "",
                                tooltip: "Icon tooltip",
                                iconType: eIconType.fontAwesome,
                                id: "Icon " + getRandomSmallKey(),
                                iconColor: "#2c2c2c",
                                nestedItems: []
                            } as ToolbarIconInfo;
                        }
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Height",
                        dataPath: "toolbarHeight",
                        minValue: 20,
                        maxValue: 80,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.Slider,
                        label: "Icon Size",
                        dataPath: "toolbarIconSize",
                        minValue: 14,
                        maxValue: 50,
                        stepValue: 1,
                        valuePosfix: "px",
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: 'Default Icons Color',
                        tooltip: 'Set value for default Icons.',
                        dataPath: "defaultIconsColor",
                    },
                ]
            },
            {
                name: "",
                elements: [
                    {
                        type: SettingsType.DropDown,
                        label: labels[TSSavedDataChartUILabelKey.ChartType],
                        tooltip: 'Default chart type. Available charts: line, line with points, scatter and bar.',
                        dataPath: "chartType",
                        items: [
                            { value: TSChartType.line, label: labels[TSSavedDataChartUILabelKey.ChartTypeLine] },
                            { value: TSChartType.scatter, label: labels[TSSavedDataChartUILabelKey.ChartTypeScatter] },
                            { value: TSChartType.bar, label: labels[TSSavedDataChartUILabelKey.ChartTypeBar] },
                            { value: TSChartType.lineWithPoints, label: labels[TSSavedDataChartUILabelKey.ChartTypeLineWithPoints] }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.GridLines],
                        tooltip: 'Whether to show or hide grid lines in charts.',
                        dataPath: "showGridLines"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowStats],
                        tooltip: "Whether to show values' statistics or not. If enabled, the minimum, maximum and average values will be shown.",
                        dataPath: "showStats"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: labels[TSSavedDataChartUILabelKey.minColor],
                        tooltip: 'If stats are shown, you can choose the color for the minimum value.',
                        dataPath: "minColor",
                        isHidden: (data: TimeSeriesSavedDataChartProps) => !data.showStats
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: labels[TSSavedDataChartUILabelKey.maxColor],
                        tooltip: 'If stats are shown, you can also choose the color for the maximum value.',
                        dataPath: "maxColor",
                        isHidden: (data: TimeSeriesSavedDataChartProps) => !data.showStats
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowDateRange],
                        tooltip: 'If enabled, a date range picker will be shown in the toolbar.',
                        dataPath: "showTimeRange"
                    },
                    {
                        type: SettingsType.Number,
                        label: labels[TSSavedDataChartUILabelKey.initialTimeRangeDays],
                        tooltip: 'Specify the initial period of the range in days. Default value is set to 30 days, meaning that when you load the page, the range will span from current day to 30 days before.',
                        dataPath: "initialTimeRangeDays",
                        oneLine: true
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowMarkersOnHover],
                        tooltip: "Whether to show markers' details on hover or show always.",
                        dataPath: "ShowMarkersOnHover"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowValueList],
                        tooltip: 'Whether to show value list or not.',
                        dataPath: "showValueList"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowFloatingLabels],
                        tooltip: 'Display labels on floating overview.',
                        dataPath: "showFloatingLabels"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.ShowOnlyCurrentFloatingLabel],
                        tooltip: 'Display floating label that contain info only for the current point.',
                        dataPath: "showOnlyCurrentPointFloatingLabel"
                    },
                    {
                        type: SettingsType.ColorPicker,
                        label: labels[TSSavedDataChartUILabelKey.FloatingLabelsInfoFontColor],
                        tooltip: 'Change font color on floating labels info.',
                        dataPath: "floatingInfoLabelsFontColor",
                        isHidden: (data: TimeSeriesSavedDataChartProps) => !data.showFloatingLabels

                    },
                    {
                        type: SettingsType.DropDown,
                        label: labels[TSSavedDataChartUILabelKey.ViewType],
                        tooltip: 'Sets the view type. Available views: Auto Value Range, Focus Last Value and None.',
                        dataPath: "ViewType",
                        items: [
                            { value: TSViewType.none, label: labels[TSSavedDataChartUILabelKey.None] },
                            { value: TSViewType.autoValueRange, label: labels[TSSavedDataChartUILabelKey.AutoValueRange] },
                            { value: TSViewType.focusLastValue, label: labels[TSSavedDataChartUILabelKey.FocusLastValue] }
                        ]
                    },
                    {
                        type: SettingsType.Boolean,
                        label: labels[TSSavedDataChartUILabelKey.CommonCharts],
                        tooltip: 'Whether to show seperate or common chart.',
                        dataPath: "commonCharts"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Edit',
                        tooltip: 'Show/Hide Edit icon.',
                        dataPath: "showEdit"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Reset',
                        tooltip: 'Show/Hide reset icon.',
                        dataPath: "showReset"
                    },
                    {
                        type: SettingsType.Boolean,
                        label: 'Show Common Chart',
                        tooltip: 'Show/Hide common chart icon.',
                        dataPath: "showCommonChart"
                    },
                    {
                        name: "Excel Download",
                        type: SettingsType.Group,
                        elements: [
                            {
                                type: SettingsType.Boolean,
                                label: 'Excel Download',
                                tooltip: 'Allows to download an excel spreadsheet (XLSX file) with the extracted data from the chart',
                                dataPath: 'excelDownload'
                            },
                            {
                                type: SettingsType.String,
                                label: "File Name",
                                dataPath: "excelOptions.fileName",
                                tooltip: 'Define excel file name',
                                oneLine: true,
                                isHidden: (data: TimeSeriesSavedDataChartProps) => !data.excelDownload
                            },
                            {
                                type: SettingsType.String,
                                label: "Datetime Format",
                                dataPath: "excelOptions.datetimeFormat",
                                tooltip: 'Define datetime format',
                                oneLine: false,
                                isHidden: (data: TimeSeriesSavedDataChartProps) => !data.excelDownload
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Timezone",
                                tooltip: 'Choose desired timezone, between local and UTC',
                                dataPath: "excelOptions.timezone",
                                inSeperateLine: false,
                                items: [
                                    { value: ExcelTimezone.UTC, label: 'UTC' },
                                    { value: ExcelTimezone.Local, label: 'Local' }
                                ],
                                isHidden: (data: TimeSeriesSavedDataChartProps) => !data.excelDownload
                            },
                            {
                                type: SettingsType.DropDown,
                                label: "Export Format",
                                tooltip: 'Define exported file format, between xlsx, csv, or both',
                                dataPath: "excelExportFormat",
                                inSeperateLine: false,
                                items: [
                                    { value: ExcelExportFormat.Excel, label: 'Excel' },
                                    { value: ExcelExportFormat.CSV, label: 'CSV' },
                                    { value: ExcelExportFormat.Both, label: 'Both' }
                                ],
                                isHidden: (data: TimeSeriesSavedDataChartProps) => !data.excelDownload
                            },
                            {
                                type: SettingsType.Boolean,
                                label: "Use Excel native datetime",
                                tooltip: 'Define if the excel is going to have native datetimes and not strings with custom datetime format. The timezone is not working in excel with native datetime. The excel always will show local time.',
                                dataPath: "excelOptions.useNativeDatetime",
                                isHidden: (data: TimeSeriesSavedDataChartProps) => !data.excelDownload
                            },
                        ]
                    },
                ]
            }];
        return <SettingsList
            AutoSave={true}
            SettingsMap={settings}
            Data={this.state.widgetData}
            onSaveData={(data) => this.setState({ widgetData: data }, () => this.saveData())}
            Toolbar={this.props.SettingListToolbar}
        />;
    }
    // ------------------------------------------------------
    private _GetLocalizedLabels(localizationKey: string): any {
        // get default values
        const defaultValues = GetTSSeriesSavedDataSettingsLocalizationDictionary();

        // check for optional `localizationKey`
        if (!isDefined(localizationKey)) {
            // return default value
            return defaultValues;
        }

        // try get localization instance
        const localization = Localization.GetInstance(localizationKey);

        if (!isDefined(localization)) {
            DebugEx.TraceError("TSSavedDataChartSettings: passed `localizationKey`: " + localizationKey + " but could not find localization instance!");
            // fallback to defaults
            return defaultValues;
        }

        // instance found; try get text map
        const map = localization.GetTextMap(TSSeriesSavedDataSettingsLocalizationTextMapKey);
        if (!isDefined(map)) {
            DebugEx.TraceError("TSSavedDataChartSettings: passed `localizationKey`: " + localizationKey + " but could not get text map.");
            // fallback to defaults
            return defaultValues;
        }

        // ok! text map found! let's update response
        const res = defaultValues;
        _.forEach(map, (value, key) => {
            if (!IsNullOrEmpty(value))
                res[key] = value;
        });

        return res;
    }
    // ------------------------------------------------------ --- Localization
}
// =======================================================================================================